import React, { useState, useEffect } from 'react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts';
import { Tv, Car, Plane, Music, DollarSign, Clock, Percent, CreditCard } from 'lucide-react';

const products = [
  { id: 1, name: "4K Smart TV", price: 1499.99, icon: Tv },
  { id: 2, name: "Used Car", price: 15000.00, icon: Car },
  { id: 3, name: "Dream Vacation", price: 5000.00, icon: Plane },
  { id: 4, name: "Concert Package", price: 799.99, icon: Music }
];

const TrueCostOfCreditCalculator = () => {
  const [selectedProduct, setSelectedProduct] = useState(products[0]);
  const [monthlyPayment, setMonthlyPayment] = useState(100);
  const [interestRate, setInterestRate] = useState(12);
  
  // Set initial monthly payment based on product price
  useEffect(() => {
    const defaultPayment = Math.max(Math.ceil(selectedProduct.price * 0.05), 10);
    setMonthlyPayment(defaultPayment);
  }, [selectedProduct]);
  const [calculations, setCalculations] = useState({
    totalPaid: 0,
    totalInterest: 0,
    months: 0,
    paymentSchedule: []
  });

  useEffect(() => {
    calculateLoan();
  }, [selectedProduct, monthlyPayment, interestRate]);

  const calculateLoan = () => {
    const monthlyRate = interestRate / 100 / 12;
    let balance = selectedProduct.price;
    let totalPaid = 0;
    let totalInterest = 0;
    let months = 0;
    let paymentSchedule = [];

    while (balance > 0 && months < 360) { // Cap at 30 years
      months++;
      const interestPayment = balance * monthlyRate;
      const principalPayment = Math.min(monthlyPayment - interestPayment, balance);
      
      if (monthlyPayment <= interestPayment) {
        // Payment too low to cover interest
        break;
      }

      balance -= principalPayment;
      totalInterest += interestPayment;
      totalPaid += (principalPayment + interestPayment);

      paymentSchedule.push({
        month: months,
        balance: balance,
        totalPaid: totalPaid,
        totalInterest: totalInterest
      });
    }

    setCalculations({
      totalPaid: totalPaid,
      totalInterest: totalInterest,
      months: months,
      paymentSchedule: paymentSchedule
    });
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(value);
  };

  return (
    <div className="max-w-6xl mx-auto p-6 space-y-8">
      {/* Header */}
      <div className="text-center">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">True Cost of Credit Calculator</h1>
        <p className="text-gray-600">See the real impact of credit purchases over time</p>
      </div>

      {/* Product Selection */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        {products.map((product) => (
          <button
            key={product.id}
            onClick={() => setSelectedProduct(product)}
            className={`p-4 rounded-lg border-2 transition-all ${
              selectedProduct.id === product.id
                ? 'border-blue-500 bg-blue-50'
                : 'border-gray-200 hover:border-blue-300'
            }`}
          >
            <product.icon className="w-8 h-8 mx-auto mb-2 text-blue-600" />
            <div className="text-sm font-medium">{product.name}</div>
            <div className="text-lg font-bold">{formatCurrency(product.price)}</div>
          </button>
        ))}
      </div>

      {/* Controls */}
      <div className="grid md:grid-cols-2 gap-8">
        <div className="space-y-6">
          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <DollarSign className="w-4 h-4 mr-2" />
              Monthly Payment
            </label>
            <input
              type="range"
              min="10"
              max={selectedProduct.price}
              value={monthlyPayment}
              onChange={(e) => setMonthlyPayment(Number(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-gray-600">
              <span>{formatCurrency(10)}</span>
              <span className="font-medium">{formatCurrency(monthlyPayment)}</span>
              <span>{formatCurrency(selectedProduct.price)}</span>
            </div>
          </div>

          <div>
            <label className="flex items-center text-sm font-medium text-gray-700 mb-2">
              <Percent className="w-4 h-4 mr-2" />
              Interest Rate (APR)
            </label>
            <input
              type="range"
              min="0"
              max="30"
              value={interestRate}
              onChange={(e) => setInterestRate(Number(e.target.value))}
              className="w-full"
            />
            <div className="flex justify-between text-sm text-gray-600">
              <span>0%</span>
              <span className="font-medium">{interestRate}%</span>
              <span>30%</span>
            </div>
          </div>
        </div>

        {/* Results Summary */}
        <div className="bg-gray-50 rounded-lg p-6 space-y-4">
          <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Summary</h3>
          
          <div className="grid grid-cols-2 gap-4">
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="text-sm text-gray-600">Purchase Price</div>
              <div className="text-2xl font-bold text-gray-900">
                {formatCurrency(selectedProduct.price)}
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="text-sm text-gray-600">Total Interest</div>
              <div className="text-2xl font-bold text-red-600">
                {formatCurrency(calculations.totalInterest)}
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="text-sm text-gray-600">Total Cost</div>
              <div className="text-2xl font-bold text-blue-600">
                {formatCurrency(calculations.totalPaid)}
              </div>
            </div>
            
            <div className="bg-white p-4 rounded-lg shadow-sm">
              <div className="text-sm text-gray-600">Time to Pay Off</div>
              <div className="text-2xl font-bold text-gray-900">
                {calculations.months} months
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Payment Schedule Chart */}
      <div className="bg-white p-6 rounded-lg shadow-lg">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment Schedule</h3>
        <div className="h-80">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={calculations.paymentSchedule}>
              <CartesianGrid strokeDasharray="3 3" />
              <XAxis 
                dataKey="month" 
                label={{ value: 'Months', position: 'bottom' }}
              />
              <YAxis 
                label={{ value: '', angle: -90, position: 'insideLeft' }}
                tickFormatter={(value) => formatCurrency(value)}
                tick={{ fontSize: 10 }} // Reduce font size of the ticks
              />
              <Tooltip 
                formatter={(value) => formatCurrency(value)}
                labelFormatter={(label) => `Month ${label}`}
              />
              <Legend 
                layout="vertical" 
                align="right" 
                verticalAlign="middle"
                />
              <Line 
                type="monotone" 
                dataKey="balance" 
                stroke="#EF4444" 
                name="Remaining Balance"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="totalPaid" 
                stroke="#3B82F6" 
                name="Total Paid"
                strokeWidth={2}
              />
              <Line 
                type="monotone" 
                dataKey="totalInterest" 
                stroke="#F59E0B" 
                name="Total Interest"
                strokeWidth={2}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      </div>

      {/* Warning Message */}
      {calculations.months >= 360 && (
        <div className="bg-red-50 border-l-4 border-red-500 p-4">
          <div className="flex">
            <div className="flex-shrink-0">
              <CreditCard className="h-5 w-5 text-red-400" />
            </div>
            <div className="ml-3">
              <p className="text-sm text-red-700">
                Warning: With the current payment and interest rate, it will take over 30 years to pay off this purchase. 
                Consider increasing your monthly payment or finding a lower interest rate.
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TrueCostOfCreditCalculator;